/**
 * TenantUsersTab.tsx — F7a T7.5.
 *
 * Zakładka "Użytkownicy" na AdminTenantDetail. Pokazuje users z tenant DB
 * + role (super admin albo prawdziwe role z firm) + companies + 2FA + ostatnie logowanie.
 * Akcja „Firmy i role" otwiera okno z edytorem ról per firma + nadawaniem i odbieraniem dostępu
 * do firm (EditTenantUserRolesModal).
 *
 * Data: GET /api/admin/tenants/:id/users?limit=100 (cała lista naraz).
 * Filtrowanie per kolumna w nagłówku tabeli (client-side); kolumna „Rola" — szukajka po nazwie roli.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Crown, Eye, KeyRound, LockOpen, UserCog, UserPlus, Users } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/Skeleton';
import { AddTenantUserModal } from '@/components/tenant-detail/AddTenantUserModal';
import { EditTenantUserRolesModal } from '@/components/tenant-detail/EditTenantUserRolesModal';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type Role = 'super_admin' | 'manager' | 'user';

interface UserCompany {
  id: string;
  legalName: string | null;
  shortName: string | null;
  isManager: boolean;
  /** Role osoby w tej firmie, w kolejności (pierwsza = główna). */
  roles: Array<{ id: string; name: string }>;
}

interface UserRow {
  id: string;
  email: string;
  displayName: string | null;
  role: Role;
  isSuperUser: boolean;
  status: string;
  companies: UserCompany[];
  totpEnabled: boolean;
  locked: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  gcrecord: string | null;
}

interface UsersResponse {
  tenant: { id: string; slug: string; code: string };
  users: UserRow[];
  total: number;
  limit: number;
  offset: number;
  dbUnavailable: boolean;
  dbUnavailableReason?: string;
}

/** Nazwy ról osoby ze wszystkich firm, bez powtórzeń (ta sama rola w kilku firmach = raz). */
function roleNames(row: UserRow): string[] {
  const names: string[] = [];
  for (const c of row.companies) {
    for (const r of c.roles) if (!names.includes(r.name)) names.push(r.name);
  }
  return names;
}

function daysBetween(iso: string, now: Date): number {
  const then = new Date(iso);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((now.getTime() - then.getTime()) / msPerDay);
}

export interface TenantUsersTabProps {
  tenantId: string;
}

export function TenantUsersTab({ tenantId }: TenantUsersTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmResetUser, setConfirmResetUser] = useState<UserRow | null>(null);
  const [confirmUnlockUser, setConfirmUnlockUser] = useState<UserRow | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [confirmSuperUser, setConfirmSuperUser] = useState<UserRow | null>(null);
  const [editRolesUser, setEditRolesUser] = useState<UserRow | null>(null);

  // Super admin = pełny dostęp do wszystkich firm tenanta (jak właściciel). Nadanie/odebranie
  // zawsze z potwierdzeniem; serwer nie pozwala odebrać ostatniemu super adminowi.
  const superUserMutation = useMutation({
    mutationFn: async (user: UserRow) => {
      const res = await api.put(`/admin/tenants/${tenantId}/users/${user.id}/super-user`, {
        isSuperUser: !user.isSuperUser,
      });
      return res.data;
    },
    onSuccess: (_data, user) => {
      toast.show({
        variant: 'success',
        title: t(
          user.isSuperUser
            ? 'admin.tenants.detail.users.superUser.toast.revoked'
            : 'admin.tenants.detail.users.superUser.toast.granted',
          { email: user.email },
        ),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      toast.show({
        variant: code === 'LAST_SUPER_USER' ? 'warning' : 'error',
        title:
          code === 'LAST_SUPER_USER'
            ? t('admin.tenants.detail.users.superUser.toast.lastSuperUser')
            : code === 'TENANT_UPDATING'
              ? t('admin.tenants.dbUpdatingError')
              : t('admin.tenants.detail.users.superUser.toast.error'),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async (user: UserRow) => {
      const res = await api.post(`/admin/tenants/${tenantId}/users/${user.id}/unlock`);
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.detail.users.unlock.toast.done'),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        const code = err.response?.data?.error as string | undefined;
        if (code === 'NOT_LOCKED') {
          toast.show({
            variant: 'warning',
            title: t('admin.tenants.detail.users.unlock.toast.notLocked'),
          });
          void queryClient.invalidateQueries({
            queryKey: ['admin', 'tenants', tenantId, 'users'],
          });
          return;
        }
        if (err.response?.status === 503) {
          toast.show({
            variant: 'error',
            title:
              code === 'TENANT_UPDATING'
                ? t('admin.tenants.dbUpdatingError')
                : t('admin.tenants.detail.users.unlock.toast.dbUnavailable'),
          });
          return;
        }
      }
      toast.show({
        variant: 'error',
        title: t('admin.tenants.detail.users.unlock.toast.error'),
      });
    },
  });

  const triggerResetMutation = useMutation({
    mutationFn: async (user: UserRow) => {
      const res = await api.post(
        `/admin/tenants/${tenantId}/users/${user.id}/trigger-password-reset`,
      );
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.detail.users.triggerReset.toast.sent'),
      });
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        const code = err.response?.data?.error as string | undefined;
        const status = err.response?.status;
        if (status === 429) {
          toast.show({
            variant: 'warning',
            title: t('admin.tenants.detail.users.triggerReset.toast.rateLimited'),
          });
          return;
        }
        if (code === 'USER_NOT_ACTIVE') {
          toast.show({
            variant: 'warning',
            title: t('admin.tenants.detail.users.triggerReset.toast.notActive'),
          });
          return;
        }
        if (code === 'TENANT_NOT_FOUND' || code === 'USER_NOT_FOUND') {
          toast.show({
            variant: 'error',
            title: t('admin.tenants.detail.users.triggerReset.toast.notFound'),
          });
          return;
        }
      }
      toast.show({
        variant: 'error',
        title: t('admin.tenants.detail.users.triggerReset.toast.error'),
      });
    },
  });

  // Filtrowanie „jak w katalogu modulow" — cale w naglowkach kolumn (client-side).
  // Pobieramy do gornego limitu, zeby filtry per kolumna obejmowaly pobrana liste.
  const query = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'users'],
    queryFn: async (): Promise<UsersResponse> => {
      const res = await api.get<UsersResponse>(`/admin/tenants/${tenantId}/users?limit=100`);
      return res.data;
    },
  });

  const statusColor = (status: string): BadgeColor => {
    if (status === 'active') return 'green';
    if (status === 'suspended') return 'red';
    if (status === 'invited') return 'amber';
    return 'gray';
  };

  const statusLabel = (status: string): string => {
    const key = `admin.tenants.detail.users.status.${status}`;
    const translated = t(key);
    return translated === key ? t('admin.tenants.detail.users.status.unknown') : translated;
  };

  const lastLoginLabel = (iso: string | null): string => {
    if (!iso) return t('admin.tenants.detail.users.lastLogin.never');
    const days = daysBetween(iso, new Date());
    if (days <= 0) return t('admin.tenants.detail.users.lastLogin.today');
    if (days === 1) return t('admin.tenants.detail.users.lastLogin.yesterday');
    return t('admin.tenants.detail.users.lastLogin.daysAgo', { count: days });
  };

  const columns = useMemo<ColumnDefinition<UserRow>[]>(
    () => [
      {
        id: 'email',
        accessor: 'email',
        header: t('admin.tenants.detail.users.columns.email'),
        sortable: true,
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">{row.email}</span>
        ),
      },
      {
        id: 'displayName',
        accessor: (row) => row.displayName ?? '',
        header: t('admin.tenants.detail.users.columns.displayName'),
        renderCell: (_value, row) =>
          row.displayName ? (
            <span className="text-text-secondary">{row.displayName}</span>
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        id: 'role',
        // Prawdziwe role z firm (nazwy złączone spacją — szukajka tekstowa po nazwie roli);
        // super admin ma pełny dostęp niezależnie od ról, więc pokazujemy go na pierwszym miejscu.
        accessor: (row) =>
          [
            row.isSuperUser ? t('admin.tenants.detail.users.role.super_admin') : '',
            ...roleNames(row),
          ].join(' '),
        header: t('admin.tenants.detail.users.columns.role'),
        sortable: true,
        renderCell: (_value, row) => {
          const names = roleNames(row);
          if (!row.isSuperUser && names.length === 0) {
            return (
              <span className="text-xs text-text-muted">
                {t('admin.tenants.detail.users.role.none')}
              </span>
            );
          }
          return (
            <div className="flex flex-wrap gap-1">
              {row.isSuperUser && (
                <Badge color="accent">{t('admin.tenants.detail.users.role.super_admin')}</Badge>
              )}
              {names.map((name) => (
                <Badge key={name} color="gray">
                  {name}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: 'companies',
        // Nazwy firm złączone spacją — szukajka tekstowa po skrótach/nazwach.
        accessor: (row) => row.companies.map((c) => c.shortName ?? c.legalName ?? '').join(' '),
        header: t('admin.tenants.detail.users.columns.companies'),
        renderCell: (_value, row) => {
          if (row.companies.length === 0) {
            return (
              <span className="text-text-muted">{t('admin.tenants.detail.users.noCompanies')}</span>
            );
          }
          return (
            <div className="flex flex-wrap gap-1">
              {row.companies.map((c) => {
                const base = t(
                  c.isManager
                    ? 'admin.tenants.detail.users.companyTooltip.manager'
                    : 'admin.tenants.detail.users.companyTooltip.member',
                  { company: c.legalName ?? c.shortName ?? '—' },
                );
                // Role w tej konkretnej firmie — w kolumnie „Rola" są zsumowane ze wszystkich firm.
                const title =
                  c.roles.length > 0 ? `${base} — ${c.roles.map((r) => r.name).join(', ')}` : base;
                return (
                  <span key={c.id} title={title}>
                    <Badge color={c.isManager ? 'blue' : 'gray'}>
                      {c.shortName ?? c.legalName ?? '—'}
                    </Badge>
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        id: 'twoFa',
        accessor: (row) => row.totpEnabled,
        header: t('admin.tenants.detail.users.columns.twoFa'),
        // Dwustanowe → przełącznik Wszystkie/Tak/Nie (client-side).
        headerFilter: { category: 'boolean' },
        renderCell: (_value, row) => (
          <Badge color={row.totpEnabled ? 'green' : 'gray'}>
            {row.totpEnabled
              ? t('admin.tenants.detail.users.twoFa.enabled')
              : t('admin.tenants.detail.users.twoFa.disabled')}
          </Badge>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.tenants.detail.users.columns.status'),
        // Stany rozłączne → dropdown wyboru (client-side).
        headerFilter: {
          category: 'select',
          options: [
            { value: 'active', label: statusLabel('active') },
            { value: 'invited', label: statusLabel('invited') },
            { value: 'suspended', label: statusLabel('suspended') },
          ],
        },
        renderCell: (_value, row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge color={statusColor(row.status)}>{statusLabel(row.status)}</Badge>
            {row.locked && (
              <Badge color="red">{t('admin.tenants.detail.users.unlock.badge')}</Badge>
            )}
          </div>
        ),
      },
      {
        id: 'lastLogin',
        accessor: (row) => (row.lastLoginAt ? new Date(row.lastLoginAt).getTime() : 0),
        header: t('admin.tenants.detail.users.columns.lastLogin'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{lastLoginLabel(row.lastLoginAt)}</span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  // Akcje jako kompaktowe przyciski-ikony z tooltipem (title) — długie etykiety
  // („Wyślij link resetu hasła") rozpychały kolumnę i wymuszały poziomy scroll tabeli.
  const renderActions = (row: UserRow) => (
    <div className="flex items-center justify-end gap-0.5">
      {row.locked && (
        <Button
          variant="ghost"
          size="sm"
          className="px-2"
          disabled={unlockMutation.isPending}
          onClick={() => setConfirmUnlockUser(row)}
          title={t('admin.tenants.detail.users.unlock.title')}
          aria-label={t('admin.tenants.detail.users.unlock.btn')}
          data-testid={`tenant-user-unlock-${row.id}`}
        >
          <LockOpen size={16} aria-hidden="true" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="px-2"
        disabled={row.status !== 'active' || triggerResetMutation.isPending}
        onClick={() => setConfirmResetUser(row)}
        title={
          row.status !== 'active'
            ? t('admin.tenants.detail.users.triggerReset.disabledNotActive')
            : t('admin.tenants.detail.users.triggerReset.title')
        }
        aria-label={t('admin.tenants.detail.users.triggerReset.btn')}
        data-testid={`tenant-user-trigger-reset-${row.id}`}
      >
        <KeyRound size={16} aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="px-2"
        disabled={row.gcrecord !== null}
        onClick={() => setEditRolesUser(row)}
        title={t('admin.tenants.detail.users.roles.btn')}
        aria-label={t('admin.tenants.detail.users.roles.btn')}
        data-testid={`tenant-user-edit-roles-${row.id}`}
      >
        <UserCog size={16} aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={row.isSuperUser ? 'px-2 text-accent-600' : 'px-2'}
        disabled={superUserMutation.isPending || row.gcrecord !== null}
        onClick={() => setConfirmSuperUser(row)}
        title={t(
          row.isSuperUser
            ? 'admin.tenants.detail.users.superUser.revokeBtn'
            : 'admin.tenants.detail.users.superUser.grantBtn',
        )}
        aria-label={t(
          row.isSuperUser
            ? 'admin.tenants.detail.users.superUser.revokeBtn'
            : 'admin.tenants.detail.users.superUser.grantBtn',
        )}
        data-testid={`tenant-user-super-user-${row.id}`}
      >
        <Crown size={16} aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="px-2"
        disabled
        title={t('admin.tenants.detail.users.detailsDisabledTooltip')}
        aria-label={t('admin.tenants.detail.users.details')}
      >
        <Eye size={16} aria-hidden="true" />
      </Button>
    </div>
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
      >
        <p>{t('admin.tenants.detail.users.loadError')}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={() => void query.refetch()}>
          {t('admin.tenants.detail.users.retry')}
        </Button>
      </div>
    );
  }

  const data = query.data;

  if (data?.dbUnavailable) {
    return (
      <div
        role="status"
        className="rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
      >
        {data?.dbUnavailableReason === 'TENANT_UPDATING'
          ? t('admin.tenants.dbUpdatingNotice')
          : t('admin.tenants.detail.users.dbUnavailable')}
      </div>
    );
  }

  const rows = data?.users ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('admin.tenants.detail.users.title')}
          </h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {t('admin.tenants.detail.users.subtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAddUserOpen(true)}
          data-testid="tenant-users-add-btn"
        >
          <UserPlus size={16} aria-hidden="true" className="mr-1.5" />
          {t('admin.tenants.detail.users.create.addButton')}
        </Button>
      </div>

      <PreviewTenantAuthBoundary>
        <ConfigurableTable<UserRow>
          tableId="admin-tenant-users"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          renderActions={renderActions}
          actionsColumnLabel={t('admin.tenants.detail.users.columns.actions')}
          persistConfig={false}
          showViewModeToggle={false}
          emptyIcon={Users}
          emptyTitle={t('admin.tenants.detail.users.empty')}
          emptyDescription=""
        />
      </PreviewTenantAuthBoundary>

      <ConfirmDialog
        open={confirmResetUser !== null}
        title={t('admin.tenants.detail.users.triggerReset.confirmTitle')}
        message={t('admin.tenants.detail.users.triggerReset.confirmBody', {
          email: confirmResetUser?.email ?? '',
        })}
        variant="primary"
        confirmLabel={t('admin.tenants.detail.users.triggerReset.btn')}
        onConfirm={() => {
          const u = confirmResetUser;
          setConfirmResetUser(null);
          if (u) triggerResetMutation.mutate(u);
        }}
        onClose={() => setConfirmResetUser(null)}
      />

      <ConfirmDialog
        open={confirmUnlockUser !== null}
        title={t('admin.tenants.detail.users.unlock.confirmTitle')}
        message={t('admin.tenants.detail.users.unlock.confirmBody', {
          email: confirmUnlockUser?.email ?? '',
        })}
        variant="primary"
        confirmLabel={t('admin.tenants.detail.users.unlock.btn')}
        onConfirm={() => {
          const u = confirmUnlockUser;
          setConfirmUnlockUser(null);
          if (u) unlockMutation.mutate(u);
        }}
        onClose={() => setConfirmUnlockUser(null)}
      />

      <ConfirmDialog
        open={confirmSuperUser !== null}
        title={t(
          confirmSuperUser?.isSuperUser
            ? 'admin.tenants.detail.users.superUser.revokeTitle'
            : 'admin.tenants.detail.users.superUser.grantTitle',
        )}
        message={t(
          confirmSuperUser?.isSuperUser
            ? 'admin.tenants.detail.users.superUser.revokeBody'
            : 'admin.tenants.detail.users.superUser.grantBody',
          { email: confirmSuperUser?.email ?? '' },
        )}
        variant={confirmSuperUser?.isSuperUser ? 'danger' : 'primary'}
        confirmLabel={t(
          confirmSuperUser?.isSuperUser
            ? 'admin.tenants.detail.users.superUser.revokeBtn'
            : 'admin.tenants.detail.users.superUser.grantBtn',
        )}
        onConfirm={() => {
          const u = confirmSuperUser;
          setConfirmSuperUser(null);
          if (u) superUserMutation.mutate(u);
        }}
        onClose={() => setConfirmSuperUser(null)}
      />

      <EditTenantUserRolesModal
        tenantId={tenantId}
        user={editRolesUser}
        onClose={() => setEditRolesUser(null)}
      />

      <AddTenantUserModal
        tenantId={tenantId}
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
      />
    </div>
  );
}
